import { cx } from './cx';

/**
 * A photograph, sized for the connection this audience actually has.
 *
 * The rule the rest of the codebase already follows: the type stack is a
 * system stack because a webfont is a render-blocking download on 4G. A
 * photograph is a much larger one, so nothing here ships a single file and
 * hopes. Each image is emitted at three widths as WebP by
 * `scripts/build-images.mjs`, and the browser picks; the source frames were
 * 1.9 MB PNGs and the largest file served is 68 KB.
 *
 * `width` and `height` are the intrinsic pixels of the source, not the
 * rendered size. They are here to reserve the box: without them the page
 * reflows when the bytes land, which on a slow connection is both the most
 * visible and the most likely moment for somebody to mis-tap a control.
 */
export function Photo({
  name,
  widths,
  width,
  height,
  alt,
  sizes,
  priority = false,
  className,
}: {
  /** File stem under `/images`, e.g. `hero-factory-team`. */
  name: string;
  /** Every rendered width, ascending. The last is the fallback `src`. */
  widths: number[];
  /** Intrinsic width of the source, for the aspect ratio. */
  width: number;
  /** Intrinsic height of the source. */
  height: number;
  /**
   * Required, and not optional-with-a-default: an empty alt is a decision
   * ("this image says nothing the text does not"), and it should have to be
   * made deliberately rather than by forgetting the prop.
   */
  alt: string;
  /** What the CSS will actually do, so the browser can choose before layout. */
  sizes: string;
  /** True for the one image in the first screenful. Everything else waits. */
  priority?: boolean;
  className?: string;
}) {
  const largest = widths[widths.length - 1];

  /**
   * Lowercase `fetchpriority`, not React's camelCase `fetchPriority`.
   *
   * React 18 does not know the camelCase prop: it warns on every render and
   * then passes it through lowercased anyway, so the attribute was always
   * correct and the console was always noisy — on production, on every page
   * carrying a photograph. Written the way it reaches the DOM, the warning goes
   * and nothing else changes. React 19 accepts either spelling.
   */
  const priorityAttrs = priority ? { fetchpriority: 'high' as const } : {};

  return (
    <img
      src={`/images/${name}-${largest}.webp`}
      srcSet={widths.map((w) => `/images/${name}-${w}.webp ${w}w`).join(', ')}
      sizes={sizes}
      width={width}
      height={height}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      {...priorityAttrs}
      decoding="async"
      className={cx('object-cover', className)}
    />
  );
}
